//@ts-nocheck

import { addTravelDays } from '../../../../utils/travelDate';

/**
 * Get accommodation details including check-in and check-out dates
 * @param {Object} lineItem - The accommodation LineItem
 * @param {Date} lineItemDayDate - The date of the LineItem's day in the itinerary
 * @returns {Object} - Object with checkInDate and checkOutDate
 */
export const getAccommodationDetails = (lineItem, lineItemDayDate) => {
  if (!lineItem || !lineItemDayDate) {
    return { checkInDate: null, checkOutDate: null };
  }

  const checkInDate = lineItemDayDate;
  const nights = parseInt(lineItem.enddayoffset || 0, 10);
  const checkOutDate = nights > 0 ? addTravelDays(checkInDate, nights) : null;

  return { checkInDate, checkOutDate };
};

/**
 * Creates a checkout LineItem for accommodations
 * @param {Object} lineItem - The accommodation LineItem
 * @param {Date} lineItemDayDate - The date of the LineItem's original day
 * @returns {Object} - Checkout LineItem object
 */
export const createCheckoutLineItem = (lineItem, lineItemDayDate) => {
  const { checkOutDate } = getAccommodationDetails(lineItem, lineItemDayDate);

  return {
    lineitemid: `checkout-${lineItem?.lineitemid}`,
    isCheckout: true,
    originalLineItemId: lineItem?.lineitemid,
    servicetypecode: 'HTL',
    servicetypename: 'Checkout',
    servicetypeicon: 'SignOut',
    title: lineItem?.title || '',
    vendorname: lineItem?.vendorname || '',
    startlocation: lineItem?.startlocation || '',
    calculatedCheckOut: checkOutDate,
    // Preserve image and attachment arrays (empty for checkout)
    lineitemimage: [],
    lineitemattachment: [],
    endtime: lineItem?.endtime || null
  };
};

/**
 * Checks if checkout should be shown on this day for the given accommodation
 * @param {Object} lineItem - The accommodation LineItem
 * @param {Date} currentDayDate - The current day's date
 * @param {Date} lineItemDayDate - The date of the LineItem's original day
 * @returns {boolean} - Whether checkout should be shown
 */
export const shouldShowCheckoutOnDay = (
  lineItem,
  currentDayDate,
  lineItemDayDate
) => {
  if (!currentDayDate || !lineItemDayDate) return false;

  const { checkOutDate } = getAccommodationDetails(lineItem, lineItemDayDate);

  return Boolean(checkOutDate) && currentDayDate === checkOutDate;
};

/**
 * Finds accommodations that have checkout on the specified day
 * @param {Array} allDays - All days from the trip with their LineItems
 * @param {Date} currentDayDate - The date to check for checkouts
 * @param {Date} tripStartDate - The trip's start date
 * @returns {Array} - Array of checkout LineItems
 */
export const getCheckoutLineItemsForDay = (
  allDays = [],
  currentDayDate,
  tripStartDate
) => {
  const checkoutLineItems = [];

  allDays?.forEach((day) => {
    const { lineitem = [], daynumber } = day;
    const lineItemDayDate = addTravelDays(tripStartDate, daynumber - 1);

    lineitem?.forEach((item) => {
      // Check if this is an accommodation LineItem
      const isAccommodation = item?.servicetypecode === 'HTL';

      if (!isAccommodation) return;

      // Check if checkout should be shown on current day
      if (
        lineItemDayDate &&
        shouldShowCheckoutOnDay(item, currentDayDate, lineItemDayDate)
      ) {
        checkoutLineItems.push(createCheckoutLineItem(item, lineItemDayDate));
      }
    });
  });

  return checkoutLineItems;
};
